import PropTypes from "prop-types";
import { AlertCircle } from "lucide-react";

/**
 * Accessible text-input primitive for the donation form.
 *
 * - Real associated <label> (visually hidden via `hideLabel` where the design
 *   wants a placeholder-only look, but always present for screen readers).
 * - Wires aria-invalid + aria-describedby to the error and hint nodes.
 * - `registration` is the object returned by react-hook-form's register(name).
 *
 * @param {object}  props
 * @param {string}  props.id                 Unique input id (also drives label htmlFor + aria ids).
 * @param {string}  props.label              Human label text.
 * @param {string=} props.error              Field error message, if any.
 * @param {string=} props.hint               Optional helper text shown under the field.
 * @param {boolean=} props.required          Marks the field visually + via aria-required.
 * @param {boolean=} props.hideLabel         Render the label visually hidden (sr-only).
 * @param {object=} props.registration       react-hook-form register() output.
 * @param {string=} props.className          Extra classes for the <input>.
 */
export default function Field({
  id,
  label,
  error,
  hint,
  required = false,
  hideLabel = false,
  registration,
  className = "",
  ...inputProps
}) {
  const errorId = error ? `${id}-error` : null;
  const hintId = hint ? `${id}-hint` : null;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  return (
    <div>
      <label
        htmlFor={id}
        className={
          hideLabel ? "sr-only" : "mb-1.5 block text-sm font-medium text-[#33413f]"
        }
      >
        {label}
        {required && (
          <span className="text-[#9d1f1c]" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </label>
      <input
        id={id}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        className={`w-full rounded-md border bg-white px-4 py-3 focus:border-[#9d1f1c] focus:ring-[#9d1f1c]/20 ${
          error ? "border-red-500" : "border-[#dfe8e6]"
        } ${className}`}
        {...registration}
        {...inputProps}
      />
      {hint && (
        <p id={hintId} className="mt-2 text-xs leading-5 text-[#52615f]">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-2 flex items-center gap-2 text-sm text-red-600">
          <AlertCircle size={15} aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}

Field.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  error: PropTypes.string,
  hint: PropTypes.string,
  required: PropTypes.bool,
  hideLabel: PropTypes.bool,
  registration: PropTypes.object,
  className: PropTypes.string,
};
